import React, { memo, useState, useRef, useEffect } from 'react';
import { TaskStatus } from '../../types';

// ============================================
// Types
// ============================================

interface TaskMoveMenuProps {
  currentStatus: TaskStatus;
  onMove: (newStatus: TaskStatus) => void;
  disabled?: boolean;
}

// ============================================
// Component
// ============================================

export const TaskMoveMenu: React.FC<TaskMoveMenuProps> = memo(({ 
  currentStatus, 
  onMove,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMove = (status: TaskStatus) => {
    if (status !== currentStatus) {
      onMove(status);
    }
    setIsOpen(false);
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const formatStatus = (status: TaskStatus): string => {
    return status.replace('_', ' ');
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button 
        onClick={handleToggle}
        disabled={disabled}
        className="
          p-1.5 text-slate-400 hover:text-indigo-600 
          bg-slate-50 rounded-lg transition-colors
          disabled:opacity-50 disabled:cursor-not-allowed
          md:hidden
        "
        aria-label="Move task to different status"
        aria-expanded={isOpen}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      </button>
      
      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div 
            className="fixed inset-0 z-[110]" 
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
            aria-hidden="true"
          />
          
          {/* Menu */}
          <div className="
            absolute right-0 top-full mt-2 w-48 
            bg-white rounded-2xl shadow-2xl border border-slate-100 
            p-2 z-[120] 
            animate-in slide-in-from-top-2 duration-200
          ">
            <p className="px-3 py-2 text-[8px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50 mb-1">
              Move Task to
            </p>
            
            {Object.values(TaskStatus).map(status => {
              const isCurrentStatus = currentStatus === status;
              
              return (
                <button 
                  key={status}
                  disabled={isCurrentStatus}
                  onClick={(e) => { e.stopPropagation(); handleMove(status); }}
                  className={`
                    w-full text-left px-3 py-2 rounded-xl 
                    text-[10px] font-bold transition-all
                    ${isCurrentStatus 
                      ? 'bg-indigo-50 text-indigo-400 cursor-default' 
                      : 'hover:bg-slate-50 text-slate-700'
                    }
                  `}
                >
                  {formatStatus(status)}
                  {isCurrentStatus && (
                    <span className="ml-2 text-[8px] text-indigo-300">(current)</span>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
});

TaskMoveMenu.displayName = 'TaskMoveMenu';

export default TaskMoveMenu;
